import generatedVariantsAsset from "@/assets/projects/otis-case-study/otis-ai-generated-variants.png";
import mediaPlanAsset from "@/assets/projects/otis-case-study/otis-ai-media-plan.png";
import recommendationsReportAsset from "@/assets/projects/otis-case-study/otis-ai-recommendations-report.png";
import questionFlowAsset from "@/assets/projects/otis-case-study/otis-ai-question-flow.png";
import mobilePreviewAsset from "@/assets/projects/otis-case-study/otis-mobile-preview.png";
import mobileDashboardAsset from "@/assets/projects/otis-case-study/otis-mobile-dashboard.png";
import typographySystemAsset from "@/assets/projects/otis-case-study/otis-typography-system.png";
import { CaseStudyMedia, CaseStudyReveal } from "@/components/CaseStudyMotion";

type OtisCaseStudyProps = {
  language: "en" | "bg";
  context: string;
  challenge: string;
  myRole: string[];
  learning: string;
  reflection: string;
};

const copy = {
  en: {
    overview: "Product overview",
    heroCaption: "A consolidated recommendations report turns campaign strategy into a set of visible, reviewable decisions.",
    scopeTitle: "End-to-end scope",
    scopeItems: ["Product Strategy", "UX Research", "User Flows & IA", "Interaction Design", "UI Design", "Prototyping", "Design Systems", "Testing & Iteration", "Product Handoff"],
    contextTitle: "Simplifying multi-channel advertising",
    contextBody: "Setting up digital advertising across Meta, Google, LinkedIn and TikTok can require decisions about channels, audiences, locations, budgets and creative formats. The end-to-end product direction connected that problem framing to the flow architecture, interaction behavior and visual interface without hiding the choices AI was making on a team's behalf.",
    challengeTitle: "The design challenge",
    challengeBody: "The experience needed to make AI assistance useful without becoming an opaque black box. That meant defining both a clear UX structure and a UI language in which recommendations stayed understandable, editable and connected to the campaign decisions they influenced.",
    researchEyebrow: "Research & product thinking",
    researchTitle: "Frame the product before shaping the interface",
    researchBody: "Product decisions were informed by the user and business context, the complexity of campaign setup, and the constraints of multiple advertising platforms. The work focused on identifying where people needed guidance, where they needed control, and how AI recommendations could support rather than obscure a decision.",
    researchDetail: "That framing guided the information architecture, progressive flow, interaction states and visual hierarchy. Concepts were refined around clarity and control, with product and engineering collaboration keeping the proposed experience grounded in platform behavior and implementation needs.",
    directionEyebrow: "UX direction",
    directionTitle: "Ask for context progressively",
    directionBody: "Instead of exposing advertising terminology and configuration all at once, the information architecture and question flow gather business context step by step. Interaction behavior, progress cues and interface hierarchy work together so each answer narrows the next decision while preserving control.",
    directionCaption: "Progressive questions collect the inputs needed for campaign recommendations without front-loading platform complexity.",
    solutionEyebrow: "Core solution",
    solutionTitle: "Move from intent to editable campaign variants",
    solutionBody: "The campaign workspace combines platform, objective, audience, location and budget controls with generated creative options. The interaction model and visual UI keep these choices in one coherent workspace, where teams can compare variants in context and adjust the configuration before moving forward.",
    solutionCaption: "Generated ad variants stay alongside campaign settings, so creative choices remain connected to delivery decisions.",
    transparentEyebrow: "Transparent AI recommendations",
    transparentTitle: "Recommendations as choices, not commands",
    transparentBody: "The media plan surfaces budget, location, channel and advertising-spend recommendations as separate, editable inputs. Reusable interaction and visual patterns give the team a consistent starting point while keeping the reasoning and final decision visible.",
    mediaCaption: "The media plan groups recommendations by decision type and keeps an edit action available before campaign creation.",
    reportCaption: "The complete recommendation set is introduced at the start of the story as a reviewable campaign plan.",
    mobileEyebrow: "Responsive experience",
    mobileTitle: "A focused workflow beyond desktop",
    mobileBody: "The responsive experience carries campaign review, creation and performance monitoring into smaller screens. Flows and interface behavior are reorganized for scanning and touch rather than simply compressing the desktop layout.",
    previewCaption: "Campaign creative can be reviewed and continued from a narrow mobile viewport.",
    dashboardCaption: "The mobile dashboard prioritizes status and performance signals for quick checks.",
    systemEyebrow: "System thinking",
    systemTitle: "A typographic foundation for a dense product",
    systemBody: "A defined type scale and reusable interface rules support hierarchy across guided questions, controls, recommendations and reporting. The system connects UI craft to implementation, helping product and engineering maintain consistency as the experience evolves across screen sizes.",
    systemCaption: "The supporting typography specification defines a reusable hierarchy for product screens.",
    roleTitle: "My role",
    roleIntro: "I owned the product design end to end: discovery and research framing, UX architecture, user flows, interaction design, UI craft, prototyping, reusable system patterns, product and engineering collaboration, handoff, and iteration.",
    learningTitle: "Learning",
    reflectionTitle: "What I would explore next",
  },
  bg: {
    overview: "Преглед на продукта",
    heroCaption: "Обобщеният отчет с препоръки превръща стратегията на кампанията във видими решения, които могат да бъдат прегледани.",
    scopeTitle: "Обхват от край до край",
    scopeItems: ["Продуктова стратегия", "UX проучване", "Потребителски потоци и IA", "Интеракционен дизайн", "UI дизайн", "Прототипиране", "Дизайн системи", "Тестване и итерации", "Предаване към разработка"],
    contextTitle: "Опростяване на многоканалната реклама",
    contextBody: "Настройването на дигитална реклама в Meta, Google, LinkedIn и TikTok изисква решения за канали, аудитории, локации, бюджети и формати. Продуктовата посока от край до край свърза дефинирането на проблема с архитектурата на потоците, интеракциите и визуалния интерфейс, без да скрива решенията, които AI предлага от името на екипа.",
    challengeTitle: "Дизайн предизвикателството",
    challengeBody: "AI помощта трябваше да бъде полезна, без да се превръща в непрозрачна „черна кутия“. Това изискваше ясна UX структура и UI език, в които препоръките остават разбираеми, редактируеми и свързани с решенията за кампанията.",
    researchEyebrow: "Проучване и продуктово мислене",
    researchTitle: "Първо рамката на продукта, после интерфейсът",
    researchBody: "Продуктовите решения бяха информирани от потребителския и бизнес контекст, сложността при настройване на кампании и ограниченията на различните рекламни платформи. Фокусът беше върху това къде хората имат нужда от насока, къде — от контрол, и как AI препоръките могат да подкрепят, вместо да скриват решението.",
    researchDetail: "Тази рамка насочи информационната архитектура, постепенния поток, състоянията на интеракция и визуалната йерархия. Концепциите се развиваха около яснота и контрол, а съвместната работа с продукт и разработка поддържаше изживяването свързано с поведението и техническите нужди на платформата.",
    directionEyebrow: "UX посока",
    directionTitle: "Постепенно събиране на контекст",
    directionBody: "Вместо цялата рекламна терминология и конфигурация да се показват наведнъж, информационната архитектура и потокът от въпроси събират бизнес контекст стъпка по стъпка. Интеракциите, индикациите за напредък и визуалната йерархия работят заедно, така че всеки отговор стеснява следващото решение, без да отнема контрола.",
    directionCaption: "Постепенните въпроси събират нужната информация за препоръки, без да показват цялата сложност на платформите от самото начало.",
    solutionEyebrow: "Основно решение",
    solutionTitle: "От намерение към редактируеми варианти",
    solutionBody: "Работното пространство съчетава настройки за платформа, цел, аудитория, локация и бюджет с генерирани креативни варианти. Интеракционният модел и визуалният UI обединяват решенията в едно пространство, където екипите могат да сравняват варианти и да променят конфигурацията преди следващата стъпка.",
    solutionCaption: "Генерираните рекламни варианти стоят до настройките, така че креативните решения остават свързани с начина на разпространение.",
    transparentEyebrow: "Прозрачни AI препоръки",
    transparentTitle: "Препоръки като избор, не като команда",
    transparentBody: "Медийният план показва бюджета, локацията, каналите и рекламните разходи като отделни, редактируеми решения. Преизползваемите интеракционни и визуални модели дават последователна отправна точка, като запазват логиката и крайното решение видими.",
    mediaCaption: "Медийният план групира препоръките по тип решение и позволява редакция преди създаване на кампанията.",
    reportCaption: "Пълният набор от препоръки е представен в началото като план за преглед.",
    mobileEyebrow: "Адаптивно изживяване",
    mobileTitle: "Фокусиран работен поток извън десктоп",
    mobileBody: "Адаптивното изживяване пренася прегледа, създаването и наблюдението на кампании на по-малки екрани. Потоците и поведението на интерфейса са реорганизирани за бързо сканиране и докосване, а не просто свити от десктоп версията.",
    previewCaption: "Креативът на кампанията може да бъде прегледан и продължен от тесен мобилен екран.",
    dashboardCaption: "Мобилното табло дава приоритет на статуса и ключовите сигнали за бърза проверка.",
    systemEyebrow: "Системно мислене",
    systemTitle: "Типографска основа за комплексен продукт",
    systemBody: "Дефинираната типографска скала и преизползваемите правила за интерфейса поддържат йерархията във въпросите, настройките, препоръките и отчетите. Системата свързва UI изработката с реализацията и помага на продукт и разработка да запазят последователност при развитието на изживяването.",
    systemCaption: "Типографската спецификация дефинира преизползваема йерархия за продуктовите екрани.",
    roleTitle: "Моята роля",
    roleIntro: "Поех продуктовия дизайн от край до край: рамкиране на откриването и проучването, UX архитектура, потребителски потоци, интеракционен дизайн, UI изработка, прототипиране, преизползваеми системни модели, съвместна работа с продукт и разработка, предаване и итерации.",
    learningTitle: "Научено",
    reflectionTitle: "Какво бих изследвал следващо",
  },
} as const;

const panelClass = "bg-card border border-border rounded-lg p-3 sm:p-5 md:p-8";
const eyebrowClass = "text-xs font-semibold uppercase text-muted-foreground mb-3";
const headingClass = "text-2xl sm:text-3xl md:text-5xl font-bold leading-tight";
const bodyClass = "text-base md:text-lg text-muted-foreground leading-relaxed max-w-3xl";

const Figure = ({ src, alt, caption, className = "" }: { src: string; alt: string; caption: string; className?: string }) => (
  <CaseStudyReveal className={className}>
    <CaseStudyMedia caption={caption} frameClassName={panelClass}>
      <img src={src} alt={alt} loading="lazy" className="w-full h-auto object-contain" />
    </CaseStudyMedia>
  </CaseStudyReveal>
);

export const OtisCaseStudy = ({ language, context, challenge, myRole, learning, reflection }: OtisCaseStudyProps) => {
  const c = copy[language];

  return (
    <div className="space-y-20 md:space-y-32">
      <section id="overview" className="scroll-mt-24 max-w-7xl mx-auto px-6">
        <p className={eyebrowClass}>{c.overview}</p>
        <Figure
          src={recommendationsReportAsset}
          alt="Otis AI campaign recommendations report showing budget, audience, location and channel decisions"
          caption={c.heroCaption}
        />
        <CaseStudyReveal className="mt-10 border-y border-border py-6 md:py-8">
          <p className={eyebrowClass}>{c.scopeTitle}</p>
          <ul className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3">
            {c.scopeItems.map((item) => (
              <li key={item} className="text-sm md:text-base font-medium leading-snug">{item}</li>
            ))}
          </ul>
        </CaseStudyReveal>
      </section>

      <section id="challenge" className="scroll-mt-24 max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-10 md:gap-16">
        <CaseStudyReveal direction="left">
          <h2 className={headingClass}>{c.contextTitle}</h2>
          <p className={`${bodyClass} mt-5`}>{c.contextBody}</p>
          <p className={`${bodyClass} mt-4`}>{context}</p>
        </CaseStudyReveal>
        <CaseStudyReveal direction="right" delay={1}>
          <h2 className={headingClass}>{c.challengeTitle}</h2>
          <p className={`${bodyClass} mt-5`}>{c.challengeBody}</p>
          <p className={`${bodyClass} mt-4`}>{challenge}</p>
        </CaseStudyReveal>
      </section>

      <section id="research" className="scroll-mt-24 max-w-7xl mx-auto px-6 grid md:grid-cols-[0.75fr_1.25fr] gap-8 md:gap-16">
        <CaseStudyReveal direction="left">
          <p className={eyebrowClass}>{c.researchEyebrow}</p>
          <h2 className={headingClass}>{c.researchTitle}</h2>
        </CaseStudyReveal>
        <CaseStudyReveal direction="right" delay={1} className="space-y-5">
          <p className={bodyClass}>{c.researchBody}</p>
          <p className={bodyClass}>{c.researchDetail}</p>
        </CaseStudyReveal>
      </section>

      <section id="direction" className="scroll-mt-24 max-w-7xl mx-auto px-6 space-y-8">
        <CaseStudyReveal>
          <p className={eyebrowClass}>{c.directionEyebrow}</p>
          <h2 className={headingClass}>{c.directionTitle}</h2>
          <p className={`${bodyClass} mt-5`}>{c.directionBody}</p>
        </CaseStudyReveal>
        <Figure src={questionFlowAsset} alt="Otis guided business-type question flow" caption={c.directionCaption} />
      </section>

      <section id="solution" className="scroll-mt-24 max-w-7xl mx-auto px-6 space-y-8">
        <CaseStudyReveal>
          <p className={eyebrowClass}>{c.solutionEyebrow}</p>
          <h2 className={headingClass}>{c.solutionTitle}</h2>
          <p className={`${bodyClass} mt-5`}>{c.solutionBody}</p>
        </CaseStudyReveal>
        <Figure src={generatedVariantsAsset} alt="Otis generated advertising variants beside editable campaign settings" caption={c.solutionCaption} />
      </section>

      <section id="recommendations" className="scroll-mt-24 max-w-7xl mx-auto px-6 space-y-8">
        <CaseStudyReveal>
          <p className={eyebrowClass}>{c.transparentEyebrow}</p>
          <h2 className={headingClass}>{c.transparentTitle}</h2>
          <p className={`${bodyClass} mt-5`}>{c.transparentBody}</p>
        </CaseStudyReveal>
        <div className="grid md:grid-cols-[1fr_0.62fr] gap-6 items-start">
          <Figure src={mediaPlanAsset} alt="Otis AI media plan with editable channel and location recommendations" caption={c.mediaCaption} />
          <div className="border-l border-border pl-6 py-2 hidden md:block">
            <p className="text-sm text-muted-foreground leading-relaxed">{c.reportCaption}</p>
          </div>
        </div>
      </section>

      <section id="mobile" className="scroll-mt-24 bg-muted py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <CaseStudyReveal><p className={eyebrowClass}>{c.mobileEyebrow}</p><h2 className={headingClass}>{c.mobileTitle}</h2><p className={`${bodyClass} mt-5`}>{c.mobileBody}</p></CaseStudyReveal>
          <div className="mt-10 grid grid-cols-2 gap-4 sm:gap-8 md:gap-16 max-w-3xl mx-auto items-start">
            <Figure src={mobilePreviewAsset} alt="Otis campaign preview on mobile" caption={c.previewCaption} />
            <Figure src={mobileDashboardAsset} alt="Otis campaign dashboard on mobile" caption={c.dashboardCaption} />
          </div>
        </div>
      </section>

      <section id="system" className="scroll-mt-24 max-w-7xl mx-auto px-6 grid md:grid-cols-[0.8fr_1.2fr] gap-10 md:gap-16 items-start">
        <CaseStudyReveal direction="left" className="md:sticky md:top-28">
          <p className={eyebrowClass}>{c.systemEyebrow}</p>
          <h2 className={headingClass}>{c.systemTitle}</h2>
          <p className={`${bodyClass} mt-5`}>{c.systemBody}</p>
        </CaseStudyReveal>
        <Figure src={typographySystemAsset} alt="Otis product typography hierarchy specification" caption={c.systemCaption} />
      </section>

      <section id="role" className="scroll-mt-24 max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-10 md:gap-16">
        <CaseStudyReveal direction="left">
          <h2 className={headingClass}>{c.roleTitle}</h2>
          <p className={`${bodyClass} mt-5`}>{c.roleIntro}</p>
          <ul className="mt-6 space-y-3">
            {myRole.map((item) => <li key={item} className="flex gap-3 text-base md:text-lg text-muted-foreground leading-relaxed"><span aria-hidden="true">•</span>{item}</li>)}
          </ul>
        </CaseStudyReveal>
        <CaseStudyReveal direction="right" delay={1} className="space-y-10">
          {learning && <div><h2 className="text-2xl sm:text-3xl font-bold">{c.learningTitle}</h2><p className={`${bodyClass} mt-4`}>{learning}</p></div>}
          {reflection && <div className="border-t border-border pt-8"><h3 className="text-lg font-bold">{c.reflectionTitle}</h3><p className={`${bodyClass} mt-4`}>{reflection}</p></div>}
        </CaseStudyReveal>
      </section>
    </div>
  );
};